import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
// Components
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
// Icons
import { TrendingUp, Loader2, Mail, Lock, User, ArrowLeft, ArrowRight } from 'lucide-react';
// Hooks & Libs
import { toast } from '../hooks/use-toast';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import Logo from '../components/Logo';

// --- Custom CSS for Nebula Theme & Interactions (Mirrored from Login) ---
const customStyles = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');

  :root {
    --bg-navy: #020617;
  }

  body {
    background-color: var(--bg-navy);
    font-family: 'Inter', sans-serif;
  }

  /* Glassmorphism Card */
  .glass-card {
    background: rgba(30, 41, 59, 0.4);
    backdrop-filter: blur(20px);
    -webkit-backdrop-filter: blur(20px);
    border: 1px solid rgba(255, 255, 255, 0.1);
    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
    transition: transform 0.1s ease-out; /* Smooth tilt */
  }

  /* Glowing Input Fields */
  .input-glow:focus-within {
    box-shadow: 0 0 15px rgba(124, 58, 237, 0.5);
    border-color: rgba(124, 58, 237, 0.5);
  }

  /* Button Glow & Shimmer */
  .btn-pro {
    background: linear-gradient(135deg, #7c3aed 0%, #3b82f6 100%);
    box-shadow: 0 0 20px rgba(124, 58, 237, 0.4);
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    background-size: 200% 200%;
    animation: shimmer 4s ease infinite;
  }

  .btn-pro:hover {
    transform: scale(1.02);
    box-shadow: 0 0 30px rgba(124, 58, 237, 0.6);
  }

  @keyframes shimmer {
    0% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  }

  /* Entrance Animations */
  @keyframes floatUp {
    from { opacity: 0; transform: translateY(20px); }
    to { opacity: 1; transform: translateY(0); }
  }

  .animate-float-up {
    animation: floatUp 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards;
    opacity: 0;
  }

  .delay-100 { animation-delay: 100ms; }
  .delay-200 { animation-delay: 200ms; }
  .delay-300 { animation-delay: 300ms; }
  .delay-400 { animation-delay: 400ms; }

  /* Mouse Spotlight */
  .spotlight-bg {
    background: radial-gradient(circle 600px at var(--mouse-x) var(--mouse-y), rgba(56, 189, 248, 0.15), transparent 80%);
  }
`;

const Register = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    password: '',
    confirmPassword: '',
    agreedToTerms: false
  });

  // Gamified Interaction State
  const [tiltStyles, setTiltStyles] = useState({});

  useEffect(() => {
    if (isAuthenticated) navigate('/dashboard');
  }, [isAuthenticated, navigate]);

  // Handle Mouse Movement for Spotlight & Tilt (Exact replica of Login)
  useEffect(() => {
    const handleMouseMove = (e) => {
      const { clientX, clientY } = e;
      const { innerWidth, innerHeight } = window;

      // Update CSS variables for spotlight
      document.documentElement.style.setProperty('--mouse-x', `${clientX}px`);
      document.documentElement.style.setProperty('--mouse-y', `${clientY}px`);

      // Calculate Tilt (Max 10 degrees)
      const xPct = (clientX / innerWidth - 0.5) * 2; // -1 to 1
      const yPct = (clientY / innerHeight - 0.5) * 2; // -1 to 1

      setTiltStyles({
        transform: `perspective(1000px) rotateX(${yPct * -5}deg) rotateY(${xPct * 5}deg)`
      });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const handleRegister = async (e) => {
    e.preventDefault();
    if (isLoading) return;

    if (formData.password !== formData.confirmPassword) {
      toast({ title: "Passwords Mismatch", description: "Your passwords do not match.", variant: "destructive" });
      return;
    }

    if (!formData.agreedToTerms) {
      toast({ title: "Terms & Conditions", description: "You must agree to the terms and conditions to continue.", variant: "destructive" });
      return;
    }

    setIsLoading(true);
    try {
      // 1. Sign Up
      const { data, error } = await supabase.auth.signUp({
        email: formData.email,
        password: formData.password,
        options: {
          data: { full_name: formData.fullName }
        }
      });
      if (error) throw error;

      // 2. Create Profile Entry
      if (data.user) {
        try {
          await supabase.from('profiles').upsert({
            id: data.user.id,
            name: formData.fullName,
            email: formData.email,
            updated_at: new Date()
          });
        } catch (profileError) {
          console.error("Profile creation warning", profileError);
        }
      }

      toast({
        title: 'Account Created!',
        description: 'Welcome to FinAssure Pro. Redirecting...'
      });

      // Small delay for UX
      setTimeout(() => {
        sessionStorage.setItem('justLoggedIn', 'true');
        navigate('/dashboard');
      }, 1000);

    } catch (error) {
      console.error('Registration error:', error);
      toast({ title: 'Sign Up Failed', description: error.message, variant: 'destructive' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSocialLogin = async (provider) => {
    try {
      sessionStorage.setItem('justLoggedIn', 'true');
      const { error } = await supabase.auth.signInWithOAuth({
        provider: provider,
        options: { redirectTo: `${window.location.origin}/dashboard` }
      });
      if (error) throw error;
    } catch (error) {
      toast({ title: 'Login Failed', description: error.message, variant: 'destructive' });
    }
  };

  return (
    <div className="min-h-screen bg-[#020617] relative overflow-hidden flex items-center justify-center p-4 selection:bg-purple-500/30 selection:text-white">
      <style>{customStyles}</style>

      {/* Background Elements */}
      <div className="fixed inset-0 spotlight-bg pointer-events-none transition-opacity duration-500"></div>

      {/* Aurora Blobs */}
      <div className="absolute top-[-10%] right-[-5%] w-[500px] h-[500px] bg-purple-600/20 rounded-full blur-[100px] animate-pulse"></div>
      <div className="absolute bottom-[-10%] left-[-5%] w-[500px] h-[500px] bg-blue-600/20 rounded-full blur-[100px] animate-pulse delay-700"></div>

      {/* Back Button */}
      <Link to="/" className="absolute top-8 left-8 z-50 animate-float-up">
        <Button variant="ghost" className="text-slate-400 hover:text-white hover:bg-white/5 transition-all group">
          <ArrowLeft className="mr-2 group-hover:-translate-x-1 transition-transform" size={18} />
          Back
        </Button>
      </Link>

      {/* Register Card Container - 3D Tilt Context */}
      <div className="relative z-10 w-full max-w-md perspective-container animate-float-up delay-100">
        <div
          className="glass-card rounded-3xl p-8 md:p-10"
          style={tiltStyles}
        >
          {/* Header */}
          <div className="text-center mb-8">
            <div className="mb-4 flex justify-center">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-br from-purple-600/20 to-purple-900/20 rounded-full blur-md"></div>
                <img
                  src="/logo.jpg"
                  alt="FinAssure Logo"
                  className="relative w-20 h-20 object-cover rounded-full border border-purple-500/30 p-1"
                  style={{
                    filter: 'brightness(1.4) contrast(1.1) drop-shadow(0 0 25px rgba(168, 85, 247, 0.8))',
                    background: 'radial-gradient(circle, rgba(168, 85, 247, 0.15), transparent 70%)',
                  }}
                />
              </div>
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">Create Account</h1>
            <p className="text-slate-400 text-sm tracking-wide">Join thousands mastering their finance.</p>
          </div>

          {/* Form */}
          <form onSubmit={handleRegister} className="space-y-5">

            {/* Full Name */}
            <div className="space-y-1.5 animate-float-up delay-200">
              <Label className="text-xs font-bold text-slate-400 tracking-wider uppercase ml-1">Full Name</Label>
              <div className="relative group">
                <User className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 group-focus-within:text-purple-400 transition-colors" size={18} />
                <Input
                  type="text"
                  placeholder="John Doe"
                  value={formData.fullName}
                  onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                  className="pl-12 bg-slate-900/50 border-slate-700/50 text-white placeholder:text-slate-600 h-12 rounded-xl input-glow transition-all duration-300"
                  required
                />
              </div>
            </div>

            {/* Email */}
            <div className="space-y-1.5 animate-float-up delay-200">
              <Label className="text-xs font-bold text-slate-400 tracking-wider uppercase ml-1">Email</Label>
              <div className="relative group">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 group-focus-within:text-purple-400 transition-colors" size={18} />
                <Input
                  type="email"
                  placeholder="name@example.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="pl-12 bg-slate-900/50 border-slate-700/50 text-white placeholder:text-slate-600 h-12 rounded-xl input-glow transition-all duration-300"
                  required
                />
              </div>
            </div>

            {/* Password */}
            <div className="space-y-1.5 animate-float-up delay-300">
              <Label className="text-xs font-bold text-slate-400 tracking-wider uppercase ml-1">Password</Label>
              <div className="relative group">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 group-focus-within:text-purple-400 transition-colors" size={18} />
                <Input
                  type="password"
                  placeholder="Create a password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="pl-12 bg-slate-900/50 border-slate-700/50 text-white placeholder:text-slate-600 h-12 rounded-xl input-glow transition-all duration-300"
                  required
                />
              </div>
            </div>

            {/* Confirm Password */}
            <div className="space-y-1.5 animate-float-up delay-300">
              <Label className="text-xs font-bold text-slate-400 tracking-wider uppercase ml-1">Confirm Password</Label>
              <div className="relative group">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 group-focus-within:text-purple-400 transition-colors" size={18} />
                <Input
                  type="password"
                  placeholder="Repeat password"
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                  className="pl-12 bg-slate-900/50 border-slate-700/50 text-white placeholder:text-slate-600 h-12 rounded-xl input-glow transition-all duration-300"
                  required
                />
              </div>
            </div>

            {/* Terms & Conditions Checkbox */}
            <div className="flex items-center space-x-2 px-1 animate-float-up delay-300">
              <input
                type="checkbox"
                id="terms"
                checked={formData.agreedToTerms}
                onChange={(e) => setFormData({ ...formData, agreedToTerms: e.target.checked })}
                className="w-4 h-4 rounded border-slate-700 bg-slate-900/50 text-purple-600 focus:ring-purple-500/50 focus:ring-offset-0 transition-colors"
                required
              />
              <label htmlFor="terms" className="text-sm text-slate-400">
                I agree to the <Link to="/terms" className="text-purple-400 hover:text-purple-300 hover:underline">Terms & Conditions</Link>
              </label>
            </div>

            <div className="pt-2 animate-float-up delay-400">
              <Button
                type="submit"
                disabled={isLoading}
                className="w-full h-12 btn-pro text-white font-bold text-base rounded-xl group"
              >
                {isLoading ? <Loader2 className="mr-2 h-5 w-5 animate-spin" /> : (
                  <span className="flex items-center">
                    Get Started <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={18} />
                  </span>
                )}
              </Button>
            </div>
          </form>

          {/* Social / Divider */}
          <div className="relative my-8">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-white/10"></div>
            </div>
            <div className="relative flex justify-center text-xs uppercase tracking-widest">
              <span className="bg-[#0f172a] px-2 text-slate-500 rounded-full border border-white/5">Or sign up with</span>
            </div>
          </div>

          <Button
            type="button"
            onClick={() => handleSocialLogin('google')}
            variant="outline"
            className="w-full h-12 bg-white/5 border-white/10 hover:bg-white/10 hover:border-white/20 text-white rounded-xl transition-all hover:scale-[1.02] flex items-center justify-center gap-3 backdrop-blur-sm mb-6"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
            </svg>
            Google
          </Button>

          <p className="text-center text-slate-500 text-sm">
            Already have an account?{' '}
            <Link to="/login" className="text-purple-400 hover:text-purple-300 font-semibold transition-colors hover:underline decoration-purple-500/30 underline-offset-4">
              Sign in here
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Register;